import { SAVE_KEY } from '../constants';
import type { SaveData, Settings } from '../types';

const SAVE_VERSION = 1;

const defaultSettings: Settings = {
  theme: 'cat',
  masterVolume: 0.8,
  musicVolume: 0.6,
  sfxVolume: 0.9,
  fullscreen: false,
  quality: 'high',
  language: 'ro',
  screenShake: true,
  particleDensity: 1.0,
  controls: {
    p1Up: 'W', p1Down: 'S', p1Left: 'A', p1Right: 'D', p1Bomb: 'SPACE',
    p2Up: 'UP', p2Down: 'DOWN', p2Left: 'LEFT', p2Right: 'RIGHT', p2Bomb: 'ENTER',
  },
};

const defaultSave: SaveData = {
  version: SAVE_VERSION,
  highScore: 0,
  maxLevel: 0,
  unlockedLevels: [1],
  settings: { ...defaultSettings, controls: { ...defaultSettings.controls } },
  stats: { totalGames: 0, totalKills: 0, totalBombs: 0, highestLevel: 0 },
};

export class SaveSystem {
  private data: SaveData;

  constructor() {
    this.data = this.load();
  }

  private load(): SaveData {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return this.cloneDefault();
      const parsed = JSON.parse(raw) as Partial<SaveData>;
      if (parsed.version !== SAVE_VERSION) return this.cloneDefault();
      return this.merge(parsed);
    } catch {
      return this.cloneDefault();
    }
  }

  private cloneDefault(): SaveData {
    return JSON.parse(JSON.stringify(defaultSave)) as SaveData;
  }

  private merge(parsed: Partial<SaveData>): SaveData {
    const base = this.cloneDefault();
    if (typeof parsed.highScore === 'number') base.highScore = parsed.highScore;
    if (typeof parsed.maxLevel === 'number') base.maxLevel = parsed.maxLevel;
    if (Array.isArray(parsed.unlockedLevels)) base.unlockedLevels = parsed.unlockedLevels;
    if (parsed.settings) {
      Object.assign(base.settings, parsed.settings);
      if (parsed.settings.controls) {
        Object.assign(base.settings.controls, parsed.settings.controls);
      }
    }
    if (!['cat', 'penguin', 'walker'].includes(base.settings.theme)) base.settings.theme = 'cat';
    if (parsed.stats) Object.assign(base.stats, parsed.stats);
    return base;
  }

  save(): void {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
    } catch {
      // storage full or unavailable
    }
  }

  get settings(): Settings { return this.data.settings; }
  get highScore(): number { return this.data.highScore; }
  get maxLevel(): number { return this.data.maxLevel; }
  get unlockedLevels(): number[] { return this.data.unlockedLevels; }
  get stats(): SaveData['stats'] { return this.data.stats; }

  updateHighScore(score: number): void {
    if (score > this.data.highScore) {
      this.data.highScore = score;
      this.save();
    }
  }

  unlockLevel(level: number): void {
    if (!this.data.unlockedLevels.includes(level)) {
      this.data.unlockedLevels.push(level);
      if (level > this.data.maxLevel) this.data.maxLevel = level;
      this.save();
    }
  }

  updateSettings(partial: Partial<Settings>): void {
    Object.assign(this.data.settings, partial);
    this.save();
  }

  incrementStat(key: keyof SaveData['stats'], amount = 1): void {
    this.data.stats[key] += amount;
    this.save();
  }

  isLevelUnlocked(level: number): boolean {
    return this.data.unlockedLevels.includes(level);
  }

  reset(): void {
    this.data = this.cloneDefault();
    this.save();
  }
}

export const saveSystem = new SaveSystem();
